/**
 * restage-context — the studio-boot-provided live-restage seam (#113 C3).
 *
 * The Q3d spike verdict: an in-place restage works IFF the studio holds its
 * composed genome in React state and re-derives the route table on a genome
 * identity change. The boot (`main.tsx`) hoists the genome into `useState` and
 * passes a `Restage` (built with `createRestage({ mode:'in-place', recompose,
 * onStaged: setGenome })`) down through THIS context. `LiveEditPanel` (C3) reads
 * it and calls `restage(edit)`; `onStaged` fires `setGenome(next)` so the
 * affected route re-resolves in place — no reload.
 *
 * A `null` context (no boot wiring) means the panel renders a degraded "restage
 * unavailable" state rather than throwing — the surface is still mountable in
 * isolation (the harness staging / a bare test).
 *
 * @module @ubiclouds/v5-studio/agent/restage-context
 */

import { createContext, useContext, type ReactNode, type ReactElement } from 'react';
import type { Restage } from '@ubiclouds/v5-agent-runtime/restage';
import type { GenomeV3 } from '@ubiclouds/v5-sdk/genome';

/** The boot-provided live agent context: the LIVE genome + the restage seam. */
export interface AgentBootContext {
  /** The LIVE composed genome (the boot's `useState` instance C3 restages). */
  readonly genome: GenomeV3;
  /** The boot-built in-place restage seam. */
  readonly restage: Restage;
}

const RestageContext = createContext<AgentBootContext | null>(null);

/** Provide the boot's live genome + restage seam to the `/agent` subtree. */
export function RestageProvider({
  value,
  children,
}: {
  readonly value: AgentBootContext | null;
  readonly children: ReactNode;
}): ReactElement {
  return <RestageContext.Provider value={value}>{children}</RestageContext.Provider>;
}

/** Read the boot agent context (`null` when the boot did not wire one). */
export function useAgentBoot(): AgentBootContext | null {
  return useContext(RestageContext);
}

/** Read just the boot restage seam (`null` when unbound). */
export function useRestage(): Restage | null {
  return useContext(RestageContext)?.restage ?? null;
}
